import React, {FC, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import HLModal, {HLModalSizeType} from '../../shared/components/modal/HLModal';
import HLFormInput from '../../shared/components/input/HLFormInput';
import HLButton, {HLButtonStyleType} from '../../shared/components/button/HLButton';
import HLErrorMessage from '../../shared/components/error-message/HLErrorMessage';
import {HLCheckbox} from '../../shared/components/checkbox/HLCheckbox';
import {LessonStructureResponse, LessonType, LessonTypeNameMap, LessonUpdateRequest} from '../../shared/types/lesson';
import {updateLessonStructureByIdFetch} from '../../app/axios/lessonFetches';
import {processFormServerError} from "../../shared/utils/processFormServerError";
import HLLine from "../../shared/components/line/HLLine";
import classes from "./UpdateLessonModal.module.scss";
import {useAppSelector} from "../../shared/hooks/useAppSelector";
import {TeacherRole} from "../../shared/types/teacher";
import HLFormSelect from "../../shared/components/select/HLFormSelect";

interface UpdateLessonModalProps {
    open: boolean,
    setOpen: (value: boolean) => void,
    lesson: LessonStructureResponse | null,
    loadLesson: () => Promise<void>
}

const lessonTypeOptions = Object.entries(LessonTypeNameMap).map(([value, label]) => ({
    value,
    label
}));

interface UpdateLessonFormData {
    lessonDate: string;
    type: LessonType;
    conducted: boolean;
    students: UpdateLessonStudentFormData[];
    teacher: UpdateLessonTeacherFormData;
}

interface UpdateLessonStudentFormData {
    studentId: number;
    paid: boolean;
    attended: boolean;
    lessonPrice: number;

    firstName: string;
    lastName: string;
}

interface UpdateLessonTeacherFormData {
    teacherRate: number

    firstName: string;
    lastName: string;
}

const UpdateLessonModal: FC<UpdateLessonModalProps> = (
    {
        open,
        setOpen,
        lesson,
        loadLesson
    }) => {
    const {currentTeacher} = useAppSelector(state => state.auth)
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        setError,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<UpdateLessonFormData>({
        mode: 'onChange',
    });

    register('students', {
        validate: value => {
            if (!value || value.length === 0) return 'Должен быть хотя бы один ученик';
        }
    });

    useEffect(() => {
        if (lesson && open) {
            setValue('lessonDate', lesson.lessonDate);
            setValue('type', lesson.type)
            setValue('conducted', lesson.conducted);
            setValue('teacher', {
                teacherRate: lesson.teacher.teacherRate,
                firstName: lesson.teacher.firstName,
                lastName: lesson.teacher.lastName
            })
            setValue('students', lesson.students.map(student => ({
                studentId: student.id,
                paid: student.paid,
                attended: student.attended,
                lessonPrice: student.lessonPrice,
                firstName: student.firstName,
                lastName: student.lastName
            })))
        }
    }, [lesson, open, setValue]);

    const students: UpdateLessonStudentFormData[] = watch('students');
    const teacher: UpdateLessonTeacherFormData = watch('teacher');

    const onSubmit = async (data: UpdateLessonFormData) => {
        if (!lesson) return;

        try {
            const request: LessonUpdateRequest = {
                lessonDate: data.lessonDate,
                type: data.type,
                conducted: data.conducted,
                teacherRate: data.teacher.teacherRate,
                students: data.students.map(student => ({
                    studentId: student.studentId,
                    paid: student.paid,
                    attended: student.attended,
                    lessonPrice: student.lessonPrice,
                }))
            };

            await updateLessonStructureByIdFetch(lesson.id, request);
            closeModal();
            loadLesson();
        } catch (err: any) {
            processFormServerError(err, setError);
        }
    };

    const closeModal = () => {
        setOpen(false);
        reset();
    };


    if (!lesson) return null

    return (
        <HLModal open={open} onClose={closeModal} title="Изменить урок" sizeType={HLModalSizeType.MEDIUM}>
            <form onSubmit={handleSubmit(onSubmit)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <h2 className={classes.header}>Общая информация</h2>
                <HLFormInput
                    id="lessonDate"
                    type="datetime-local"
                    {...register('lessonDate', {
                        required: 'Дата урока обязательна'
                    })}
                    error={errors.lessonDate?.message}
                    placeholder="Дата и время урока"
                >Дата и время урока</HLFormInput>

                <HLFormSelect
                    id="role"
                    {...register('type', {required: 'Тип обязателен'})}
                    value={watch('type') || ''}
                    options={lessonTypeOptions}
                    error={errors.type?.message}
                />

                <HLCheckbox
                    id="conducted"
                    {...register('conducted')}
                >Проведен</HLCheckbox>

                <h2 className={classes.header}>Учитель</h2>
                {teacher && <>
                    <h4>{teacher.firstName} {teacher.lastName}</h4>
                    <HLFormInput
                        id="teacher.teacherRate"
                        type="number"
                        step="0.01"
                        {...register(`teacher.teacherRate`, {
                            required: 'Ставка учителя обязательна'
                        })}
                        error={errors.teacher?.teacherRate?.message}
                    >Ставка учителя</HLFormInput>
                </>}


                <h2 className={classes.header}>Ученики</h2>
                {students && students.map((student: UpdateLessonStudentFormData, index: number) => (
                    <div key={student.studentId} className={`columnContainer`}>
                        <h4>{student.firstName} {student.lastName || ''}</h4>
                        {currentTeacher?.role === TeacherRole.ADMIN &&
                            <HLFormInput
                                id={`students.${index}.lessonPrice`}
                                type="number"
                                step="0.01"
                                {...register(`students.${index}.lessonPrice`, {
                                    required: 'Цена обязательна'
                                })}
                                error={errors.students?.[index]?.lessonPrice?.message}
                            >Цена урока</HLFormInput>
                        }

                        <div className="inlineContainer">
                            <HLCheckbox
                                id={`students.${index}.attended`}
                                {...register(`students.${index}.attended`)}
                            >Присутствовал</HLCheckbox>
                            <HLCheckbox
                                id={`students.${index}.paid`}
                                {...register(`students.${index}.paid`)}
                            >Списан</HLCheckbox>
                        </div>

                        {students.length !== index + 1 && <HLLine/>}
                    </div>
                ))}
                <div className="inlineContainer">
                    <HLButton type="submit" disabled={isSubmitting}>Сохранить</HLButton>
                    <HLButton type="button" onClick={closeModal} styleType={HLButtonStyleType.GRAY}>Отмена</HLButton>
                </div>
            </form>
        </HLModal>
    );
};

export default UpdateLessonModal; 